import React from "react";
import { Helmet } from "react-helmet";

import Header from "../Header";
import Footer from "../Footer";
import { buildAbsolutePageUrl } from "../../utils/siteUrl";
import { PageContainer, PageContent, TextBlock } from "./styles";
import {
  InstitutionalTextBlock,
  InstitutionalTextBlockVariant,
  InstitutionalTextPageProps,
} from "./types";

const BLOCK_VARIANT_CLASS: Record<
  InstitutionalTextBlockVariant,
  string | undefined
> = {
  default: undefined,
  placeholder: "placeholder-section",
  notice: "legal-notice",
  agreement: "agreement",
};

function renderBlockBody(
  body: InstitutionalTextBlock["body"],
  variant: InstitutionalTextBlockVariant = "default"
) {
  const className = BLOCK_VARIANT_CLASS[variant];

  if (className && typeof body === "string") {
    return <p className={className}>{body}</p>;
  }

  if (className) {
    return <div className={className}>{body}</div>;
  }

  if (typeof body === "string") {
    return <p>{body}</p>;
  }

  return body;
}

export default function InstitutionalTextPage({
  path,
  title,
  description,
  lastUpdated,
  notice,
  blocks,
  children,
}: InstitutionalTextPageProps) {
  const pageUrl = buildAbsolutePageUrl(path);
  const helmetTitle = title.includes("|") ? title : `${title} | 7Cantos`;

  return (
    <PageContainer>
      <Helmet>
        <title>{helmetTitle}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={helmetTitle} />
        <meta property="og:description" content={description} />
        <meta property="twitter:title" content={helmetTitle} />
        <meta property="twitter:description" content={description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <link rel="canonical" href={pageUrl} />
      </Helmet>

      <Header />
      <PageContent>
        <h2>{title.replace(/\s*\|\s*7Cantos\s*$/i, "")}</h2>

        {lastUpdated && (
          <p className="last-update">Última atualização: {lastUpdated}</p>
        )}

        {notice && (
          <p className="legal-notice" role="note">
            {notice}
          </p>
        )}

        {blocks?.map((block, index) => (
          <TextBlock key={block.id ?? block.heading ?? String(index)}>
            {block.heading && <h3>{block.heading}</h3>}
            {renderBlockBody(block.body, block.variant)}
          </TextBlock>
        ))}

        {children}
      </PageContent>
      <Footer />
    </PageContainer>
  );
}
